// Session handling for an app that has no logout.
//
// There is no password here and no account screen — the only way in is a code
// sent to a channel printed on your own card. So an expired session is not a
// security boundary, it is just someone being asked to fetch a code again for
// no reason. Nothing is lost when one ends: cards belong to the identity, not
// to the session, and signing in again restores them.
//
// The policy, therefore:
//   - the token lives as long as the server allows (~3 years) and is REFRESHED
//     on every launch, so the window rolls forward and in practice never ends;
//   - nothing anywhere calls logout;
//   - if a session does end, the app asks for a code again rather than showing
//     an error or a login wall.
import { pb } from './pocketbase';

/** The address the signed-in person proved. Empty when nobody is signed in.
 *  Used to prefill their company's email domain — they are almost always
 *  setting up the domain they just received a code at. */
export function myEmail(): string {
  const rec = pb.authStore.record as { email?: string } | null;
  return (rec?.email ?? '').trim().toLowerCase();
}

/** Is someone signed in right now? */
export function signedIn(): boolean {
  return pb.authStore.isValid;
}

/**
 * Roll the session forward. Call once per launch.
 *
 * Returns false when there is no usable session — either there never was one,
 * or the server rejected it (revoked, expired, or the identity is gone). The
 * caller's response to false is to prompt for a code, never to show an error.
 */
export async function keepAlive(): Promise<boolean> {
  if (!pb.authStore.isValid) return false;
  try {
    // Issues a fresh token, which restarts the expiry window.
    await pb.collection('users').authRefresh();
    return true;
  } catch (err) {
    // A network failure must NOT clear the session — the person is offline, not
    // signed out, and dropping the token would strand their cards until they
    // could reach a network again. Only an explicit rejection ends it.
    if (isAuthRejection(err)) {
      pb.authStore.clear();
      return false;
    }
    return pb.authStore.isValid;
  }
}

/** Did the server actually reject the credentials, as opposed to being
 *  unreachable? Only the former should end a session. */
function isAuthRejection(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  return status === 401 || status === 403 || status === 404;
}

/** Listen for the session ending, so the app can ask for a code again instead
 *  of failing quietly. Returns an unsubscribe function. */
export function onSessionEnded(handler: () => void): () => void {
  return pb.authStore.onChange((token) => {
    if (!token) handler();
  });
}
